import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { ThemeEvents, CartAddEvent } from '@theme/events';

/**
 * A custom element that lets a customer choose one sample sachet to travel with the
 * product they are adding to the cart.
 *
 * The element is rendered with the `hidden` attribute so that a customer with
 * JavaScript disabled never sees a control that cannot work. It reveals itself on
 * connect, and the product form is untouched either way.
 *
 * Pricing is deliberately never asserted here. The first sample is discounted by an
 * automatic Buy X Get Y discount configured in the admin, so the theme adds the sample
 * at its normal price and lets the discount do the rest.
 *
 * @typedef {object} SamplePickerRefs
 * @property {HTMLInputElement[]} [inputs] - The sample radio inputs.
 * @property {HTMLElement} [status] - The live region used for feedback.
 * @property {HTMLElement[]} [tabButtons] - The category tab buttons.
 * @property {HTMLElement[]} [panels] - The per-category panels.
 * @extends Component<SamplePickerRefs>
 */
class SamplePickerComponent extends Component {
  /** @type {boolean} Guards against reacting to our own cart update. */
  #adding = false;

  /**
   * The variant this picker last reported as added, or null when nothing is claimed.
   * The confirmation message is only allowed to stand while this variant is genuinely
   * in the cart, so removing the sample anywhere else clears the message.
   * @type {string | null}
   */
  #addedVariantId = null;

  /** @type {Element | null} */
  #productForm = null;

  /** @type {string[]} */
  #categories = [];

  connectedCallback() {
    super.connectedCallback();

    this.#buildTabs();

    // Only reveal once the component is live, so the picker stays hidden without JS.
    this.hidden = false;

    this.#productForm = this.closest('product-form-component');
    this.#productForm?.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);

    // Removals happen outside this component, in the cart drawer or on the cart page,
    // so the confirmation has to be reconciled against document wide cart updates.
    document.addEventListener(ThemeEvents.cartUpdate, this.#syncStatusWithCart);
  }

  /**
   * Drops the confirmation as soon as the sample it refers to leaves the cart.
   */
  #syncStatusWithCart = async () => {
    // Our own add sets the message; let it stand.
    if (this.#adding) return;
    if (!this.#addedVariantId) return;

    if (!(await this.#isInCart(this.#addedVariantId))) this.#clearStatus();
  };

  /**
   * @param {string} variantId - The variant to look for.
   * @returns {Promise<boolean>} Whether that variant is currently in the cart.
   */
  async #isInCart(variantId) {
    try {
      const response = await fetch(`${Theme.routes.cart_url}.js`, {
        headers: { Accept: 'application/json' },
      });
      // On a failed check, leave the message alone rather than clearing it wrongly.
      if (!response.ok) return true;

      const cart = await response.json();
      return (cart.items ?? []).some((item) => String(item.variant_id) === String(variantId));
    } catch (error) {
      return true;
    }
  }

  #clearStatus() {
    const { status } = this.refs;
    if (status) status.textContent = '';
    this.#addedVariantId = null;
  }

  /**
   * Builds the category tabs from the rendered items.
   *
   * Liquid emits one flat row with a data-category on each item; deriving the tabs here
   * keeps the Liquid to a single pass over the samples, which matters because this
   * snippet runs on every full size tea PDP.
   */
  #buildTabs() {
    const tabs = this.refs.tabs;
    const items = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.sample-picker__item')]);
    if (!tabs || !items.length) return;

    const ORDER = [
      'black-tea',
      'green-tea',
      'herbal',
      'chai',
      'oolong',
      'white-tea',
      'latte-blends',
      'matcha',
      'tea-bags',
    ];
    const found = new Set(items.map((item) => item.dataset.category).filter(Boolean));
    this.#categories = ORDER.filter((category) => found.has(category));
    // Anything not in the known order still gets a tab, so nothing is unreachable.
    for (const category of found) {
      if (!this.#categories.includes(category)) this.#categories.push(category);
    }
    if (!this.#categories.length) return;

    const label = (value) => value.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());

    tabs.replaceChildren(
      ...this.#categories.map((category, index) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `sample-picker__tab${index === 0 ? ' is-active' : ''}`;
        button.setAttribute('role', 'tab');
        button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
        button.dataset.tab = category;
        button.textContent = label(category);
        button.addEventListener('click', this.selectTab);
        return button;
      })
    );

    this.#showCategory(this.#categories[0]);
  }

  /**
   * Shows only the items in the given category.
   * @param {string} category - The category tag to show.
   */
  #showCategory(category) {
    for (const item of this.querySelectorAll('.sample-picker__item')) {
      if (item instanceof HTMLElement) item.hidden = item.dataset.category !== category;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#productForm?.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.cartUpdate, this.#syncStatusWithCart);
  }

  /**
   * Switches the visible category panel. The radio group spans every panel, so a
   * selection made in one category survives switching tabs.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  selectTab = (event) => {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-tab]');
    if (!(button instanceof HTMLElement)) return;

    const target = button.dataset.tab;
    if (!target) return;

    for (const tab of this.querySelectorAll('.sample-picker__tab')) {
      const active = tab instanceof HTMLElement && tab.dataset.tab === target;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', active ? 'true' : 'false');
    }

    this.#showCategory(target);
  };

  /**
   * Returns the currently selected sample input, if any.
   * @returns {HTMLInputElement | undefined}
   */
  #selected() {
    const inputs = this.refs.inputs;
    if (!inputs) return undefined;

    return (Array.isArray(inputs) ? inputs : [inputs]).find((input) => input.checked);
  }

  /**
   * Adds the chosen sample after the product itself has been added.
   * @param {Event} event - The cart update event from the product form.
   */
  #handleCartUpdate = (event) => {
    // Ignore the update we trigger ourselves, and ignore failed adds.
    if (this.#adding) return;
    if (event instanceof CustomEvent === false && /** @type {any} */ (event).detail?.data?.didError) return;

    const selected = this.#selected();
    if (!selected) return;

    this.#addSample(selected);
  };

  /**
   * Adds a single sample to the cart.
   * @param {HTMLInputElement} input - The selected sample input.
   */
  async #addSample(input) {
    const { status } = this.refs;

    this.#adding = true;

    try {
      const body = new FormData();
      body.append('id', input.value);
      body.append('quantity', '1');

      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((item) => {
        if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
      });
      if (sectionIds.length) body.append('sections', sectionIds.join(','));

      const config = fetchConfig('javascript', { body });
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const result = await response.json();

      if (result.status) {
        if (status) status.textContent = Theme.translations?.sample_picker_error ?? '';
        return;
      }

      // Let the rest of the theme refresh the cart drawer, bubble and totals.
      this.dispatchEvent(
        new CartAddEvent({}, this.id || 'sample-picker', {
          source: 'sample-picker',
          variantId: input.value,
          sections: result.sections,
        })
      );

      // Claim the variant before showing the message, so the cart listener knows
      // which line leaving the cart should retract this confirmation.
      this.#addedVariantId = input.value;

      if (status) {
        const title = input.dataset.sampleTitle ?? '';
        status.textContent = title ? `${title} added to your order.` : '';
      }

      // Clear the choice so a repeat add does not silently duplicate it.
      input.checked = false;
    } catch (error) {
      if (status) status.textContent = '';
    } finally {
      this.#adding = false;
    }
  }
}

if (!customElements.get('sample-picker-component')) {
  customElements.define('sample-picker-component', SamplePickerComponent);
}
